import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { researchStatusAtom, setResearchStatusAtom } from '../../global-state.worker-data';
import { useWorkersService } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';
import { useToastMessageContext } from '@restworld/utils-common';

export type ResearchStatusData = {
  researchStatus: string;
};

export type UseUpdateResearchStatusFormProps = {
  onClose: () => void;
};

export function useUpdateResearchStatusForm({ onClose }: UseUpdateResearchStatusFormProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const researchStatus = useAtomValue(researchStatusAtom);
  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
    setValue,
    setError,
    reset,
  } = useForm<ResearchStatusData>({ defaultValues: { researchStatus: researchStatus?.key } });
  const workerService = useWorkersService();
  const authContext = useAuth();
  const setResearchStatus = useSetAtom(setResearchStatusAtom);

  const { setToastMessage, toastMessageOptions } = useToastMessageContext();

  async function closeTimeout() {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(undefined);
      }, 1000);
    });
  }

  const handleUpdate = useCallback(
    async (data: ResearchStatusData) => {
      setLoading(true);
      try {
        const response = await workerService.updateResearchStatus({
          workerId: authContext?.user?.id,
          statusKey: data.researchStatus,
        });
        if (response?.data) {
          setResearchStatus(response?.data?.research_status);
        }
        await closeTimeout();
        setLoading(false);
        setToastMessage({
          ...toastMessageOptions,
          message: t('onboarding-page:info_updated_correctly'),
          duration: 5000,
          visible: true,
          type: 'success',
          position: 'bottom',
          ui: 'alert',
        });
        onClose();
      } catch (err) {
        setLoading(false);
      }
    },
    [t, authContext?.user?.id, onClose, setResearchStatus, setToastMessage, toastMessageOptions, workerService]
  );

  const onSubmit = async (data: ResearchStatusData) => {
    handleUpdate(data);
  };

  useEffect(() => {
    reset({ researchStatus: researchStatus?.key });
  }, [researchStatus?.key, reset]);

  return {
    register,
    handleSubmit,
    watch,
    control,
    errors,
    setValue,
    setError,
    onSubmit,
    loading,
    setLoading,
  };
}
